import {
  type ChangeEvent,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  useEffect,
  useRef,
} from 'react'

export interface TextFieldProps
  extends Omit<HTMLProps<HTMLInputElement>, 'ref'> {
  error?: string
  name: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
}

export const TextField = forwardRef(
  (
    { error, name, onChange, value, ...rest }: TextFieldProps,
    ref?: ForwardedRef<HTMLInputElement | null>,
  ) => {
    const localRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
      if (!error) return localRef.current?.setCustomValidity('')
      localRef.current?.setCustomValidity(error)
      localRef.current?.reportValidity()
    }, [error])

    function localOnChange(e: ChangeEvent<HTMLInputElement>) {
      // Do not allow leading space
      e.target.value = e.target.value.trimStart()
      e.target.setCustomValidity('')
      onChange?.(e)
    }

    function setRef(element: HTMLInputElement | null) {
      localRef.current = element
      if (!ref) return
      if (typeof ref === 'function') ref(element)
      else ref.current = element
    }

    return (
      <input
        autoComplete="off"
        data-lpignore
        data-1p-ignore
        id={name}
        name={name}
        onChange={localOnChange}
        ref={setRef}
        type="text"
        value={value}
        {...rest}
      />
    )
  },
)
